'use client';

/**
 * Reset Password Form Component
 * Magento 2 production-style — completes a password reset from an emailed link.
 *
 * The URL fragment carries a single-use token minted by
 * `/api/auth/forgot-password`. The fragment is never sent to the server and is
 * removed from the visible URL as soon as the component reads it. There is no
 * email or code field any more: the token identifies the account, and holding
 * it is the proof of mailbox ownership.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { meetsPasswordPolicy, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/password-policy';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';

/** Whether the link in the URL is still redeemable. */
type LinkState = 'reading' | 'checking' | 'live' | 'dead' | 'absent';

/**
 * How long to wait for the liveness check before giving up and showing the
 * form anyway. Generous enough for a bad mobile connection, short enough that
 * nobody sits on a spinner holding a link that works.
 */
const LINK_CHECK_TIMEOUT_MS = 5_000;

export function ResetPasswordForm() {
  const [token, setToken] = useState('');
  const [linkState, setLinkState] = useState<LinkState>('reading');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * The token, kept across effect re-runs. Reading the fragment SCRUBS it, so
   * the read is not repeatable: a second run (StrictMode double-invoke in dev,
   * or any remount) finds an empty hash and would otherwise tell a customer
   * holding a perfectly good link that it is invalid.
   */
  const capturedToken = useRef('');

  useEffect(() => {
    // Bumped per check so a slow answer for an old link can never overwrite the
    // verdict for the one the customer is looking at now.
    let latestCheck = 0;

    const readLinkFromUrl = () => {
      const check = ++latestCheck;
      const fragment = new URLSearchParams(window.location.hash.slice(1));
      const tokenInHash = fragment.get('token') ?? '';

      if (tokenInHash) {
        capturedToken.current = tokenInHash;
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}`,
        );
      }

      // Falls back to the captured value precisely because the line above
      // removed it from the URL: only a customer who never had a token at all
      // should see the "no link" panel.
      const fragmentToken = tokenInHash || capturedToken.current;

      setToken(fragmentToken);

      if (!fragmentToken) {
        setLinkState('absent');
        return;
      }

      // The token rides in the fragment, so the server could not have checked
      // it while rendering. Ask now, before showing a password form the
      // customer cannot actually submit.
      setLinkState('checking');

      void (async () => {
        // Fails OPEN on every path that is not a definite "dead" answer, INCLUDING
        // one that never answers at all. A hung request is the dangerous case: it
        // is not an error, so no catch fires, and the customer would sit on
        // "Checking your reset link..." forever holding a link that works. Before
        // this check existed the form appeared immediately, so a stall has to
        // degrade back to exactly that rather than strand them.
        let valid = true;
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | undefined;

        try {
          valid = await Promise.race([
            (async () => {
              const response = await fetch('/api/auth/reset-password/check', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: fragmentToken }),
                signal: controller.signal,
              });

              if (!response.ok) return true;

              const data = (await response.json()) as { valid?: boolean };
              return data.valid !== false;
            })(),
            // Covers a stalled connection AND a body that never finishes
            // streaming, which an abort on its own would not.
            new Promise<boolean>((resolve) => {
              timer = setTimeout(() => {
                controller.abort();
                resolve(true);
              }, LINK_CHECK_TIMEOUT_MS);
            }),
          ]);
        } catch {
          valid = true; // Offline, blocked, aborted, or no fetch at all.
        } finally {
          clearTimeout(timer);
        }

        if (check === latestCheck) setLinkState(valid ? 'live' : 'dead');
      })();
    };

    readLinkFromUrl();

    // The expired panel tells the customer to go open a newer email. If that
    // link opens in this same tab it is a same-document fragment change, so
    // nothing remounts and they would keep staring at "expired" while holding
    // a perfectly good link. Re-read on every hash change.
    window.addEventListener('hashchange', readLinkFromUrl);

    return () => {
      latestCheck = Infinity;
      window.removeEventListener('hashchange', readLinkFromUrl);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!meetsPasswordPolicy(newPassword)) {
      setError(PASSWORD_REQUIREMENTS_MESSAGE);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="register-form-wrapper">
        <div className="account-alert account-alert--success mb-4">
          Your password has been reset successfully.
        </div>
        <div className="register-actions">
          <Link href="/login" className="register-submit-btn" style={{ textDecoration: 'none' }}>
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // 'reading' is synchronous and paints once; 'checking' waits on the network,
  // so it gets visible text. Rendering nothing there would show a blank page on
  // a slow connection, which is the complaint that started all of this.
  if (linkState === 'reading') {
    return null;
  }

  if (linkState === 'checking') {
    return (
      <div className="register-form-wrapper">
        <p className="register-fieldset-note">Checking your reset link...</p>
      </div>
    );
  }

  // Two dead ends, deliberately worded differently. "absent" means there was no
  // link at all (bookmarked page, typed URL). "dead" means they followed a real
  // link that has since expired or been used — and because a newer mail may
  // already be sitting in their inbox, say so before sending them round the
  // request loop again.
  if (linkState !== 'live') {
    return (
      <div className="register-form-wrapper">
        <div className="account-alert account-alert--warning mb-4">
          {linkState === 'dead' ? (
            <>
              <strong>This password reset link has expired.</strong>
              <br />
              Reset links last 60 minutes and can only be used once. Check your
              inbox for a more recent email from us first, as that link may
              still work. Otherwise request a new one below.
            </>
          ) : (
            'This password reset link is invalid or has expired. Request a new one.'
          )}
        </div>
        <div className="register-actions">
          <Link
            href="/forgot-password"
            className="register-submit-btn"
            style={{ textDecoration: 'none' }}
          >
            Request a New Link
          </Link>
        </div>
        <div className="register-links">
          <Link href="/login" className="register-signin-link">
            Remember your password? Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="register-form-wrapper">
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="account-alert account-alert--warning mb-4">
            {error}
          </div>
        )}

        <fieldset className="register-fieldset">
          <legend className="register-fieldset-legend">
            <span>Reset Your Password</span>
          </legend>

          <p className="register-fieldset-note">
            Choose a new password for your account.
          </p>

          <div className="register-form-group">
            <label htmlFor="reset-new-password">
              New Password <span className="required">*</span>
            </label>
            <input
              id="reset-new-password"
              aria-describedby="reset-new-password-rules"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
            <PasswordRequirements password={newPassword} id="reset-new-password-rules" />
          </div>

          <div className="register-form-group">
            <label htmlFor="reset-confirm-password">
              Confirm New Password <span className="required">*</span>
            </label>
            <input
              id="reset-confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          <div className="register-checkbox-group">
            <input
              id="reset-show-password"
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            <label htmlFor="reset-show-password">Show Password</label>
          </div>

          <p className="register-required-note">* Required Fields</p>
        </fieldset>

        <div className="register-actions">
          <button
            type="submit"
            className="register-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting password...' : 'Reset Password'}
          </button>
        </div>

        <div className="register-links">
          <Link href="/login" className="register-signin-link">
            Remember your password? Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
